import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { toast } from 'react-toastify'
import DirectionsRoundedIcon from '@mui/icons-material/DirectionsRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded'
import { SkeletonBlock, SkeletonLine } from 'components/Skeleton'
import { ink, line, radius, surface, font, transition, bp as bpToken } from 'features/dashboard/tokens'

const TILE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}'
const TILE_REFERENCE_URL = 'https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}'
const TILE_ATTRIBUTION = '&copy; Esri, HERE, Garmin &amp; OpenStreetMap contributors'
const CACHE_KEY = 'bp.postcodeGeo.v1'

const normPc = pc => String(pc || '').toUpperCase().replace(/\s+/g, '')

const readCache = () => {
  try { return JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}') } catch { return {} }
}

const writeCache = next => {
  try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(next)) } catch {}
}

const geocodePostcode = async postcode => {
  const key = normPc(postcode)
  if (!key) return null
  const cache = readCache()
  const hit = cache[key]
  if (hit && Number.isFinite(hit.lat) && Number.isFinite(hit.lng)) return hit
  const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(postcode)}`)
  if (!res.ok) return null
  const json = await res.json()
  const result = json?.result
  if (!result || result.latitude == null) return null
  const coords = { lat: result.latitude, lng: result.longitude }
  writeCache({ ...cache, [key]: coords })
  return coords
}

const LocationCard = ({ name, street, city, postalCode, uprn, accent = '#D24724', className }) => {
  const containerRef = useRef(null)
  const mapRef = useRef(null)
  const [coords, setCoords] = useState(null)
  const [status, setStatus] = useState('loading')

  const address = [street, city, postalCode].filter(Boolean).join(', ')
  const query = encodeURIComponent([name, address].filter(Boolean).join(', '))

  useEffect(() => {
    let cancelled = false
    if (!postalCode) { setStatus('none'); return undefined }
    setStatus('loading')
    geocodePostcode(postalCode)
      .then(found => {
        if (cancelled) return
        if (found) { setCoords(found); setStatus('ready') } else setStatus('none')
      })
      .catch(() => { if (!cancelled) setStatus('none') })
    return () => { cancelled = true }
  }, [postalCode])

  useEffect(() => {
    const node = containerRef.current
    if (!node || !coords || status !== 'ready') return undefined

    const map = L.map(node, {
      center: [coords.lat, coords.lng],
      zoom: 16,
      maxZoom: 16,
      minZoom: 6,
      scrollWheelZoom: false,
      zoomControl: true,
      attributionControl: true,
    })
    mapRef.current = map

    L.tileLayer(TILE_URL, { attribution: TILE_ATTRIBUTION, maxZoom: 16 }).addTo(map)
    L.tileLayer(TILE_REFERENCE_URL, { maxZoom: 16 }).addTo(map)

    const pin = L.divIcon({
      className: 'bp-location-pin',
      html: `<span style="display:block;width:18px;height:18px;border-radius:50%;background:${accent};box-shadow:0 0 0 5px ${accent}33, 0 1px 3px rgba(12,18,41,.35)"></span>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    })
    L.marker([coords.lat, coords.lng], { icon: pin, keyboard: false }).addTo(map)

    map.on('click', () => map.scrollWheelZoom.enable())
    map.on('mouseout', () => map.scrollWheelZoom.disable())

    const resize = () => map.invalidateSize()
    window.addEventListener('resize', resize)
    const t = setTimeout(resize, 200)

    return () => {
      window.removeEventListener('resize', resize)
      clearTimeout(t)
      map.remove()
      mapRef.current = null
    }
  }, [coords, status, accent])

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(address)
      toast.success('Address copied.')
    } catch {
      toast.error('Could not copy the address.')
    }
  }

  return (
    <Card className={className}>
      <MapPane>
        {status === 'loading' && <SkeletonBlock $h={260} $r='0' />}
        {status === 'ready' && <MapCanvas ref={containerRef} aria-label={`Map showing ${name || 'this building'}`} />}
        {status === 'none' && (
          <NoMap>
            <PlaceRoundedIcon style={{ fontSize: 26, opacity: 0.4 }} />
            <span>
              {postalCode
                ? 'We could not find this postcode on the map.'
                : 'Add a postcode to this Building Passport to see it on a map.'}
            </span>
          </NoMap>
        )}
      </MapPane>

      <Details>
        <DetailHead>
          <PlaceRoundedIcon style={{ fontSize: 18, color: accent }} />
          <DetailTitle>{name || 'Location'}</DetailTitle>
        </DetailHead>

        {status === 'loading' ? (
          <>
            <SkeletonLine $w='80%' $h={13} />
            <SkeletonLine $w='55%' $h={13} />
          </>
        ) : (
          <Address>{address || 'No address recorded yet.'}</Address>
        )}

        {uprn && (
          <Meta>
            <MetaLabel>UPRN</MetaLabel>
            <MetaValue>{uprn}</MetaValue>
          </Meta>
        )}

        {address && (
          <Actions>
            <PrimaryAction
              href={`https://www.google.com/maps/dir/?api=1&destination=${query}`}
              target='_blank'
              rel='noopener noreferrer'
              $accent={accent}
            >
              <DirectionsRoundedIcon style={{ fontSize: 17 }} />
              Get directions
            </PrimaryAction>
            <GhostAction
              href={`https://www.google.com/maps/search/?api=1&query=${query}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <OpenInNewRoundedIcon style={{ fontSize: 16 }} />
              Open in Maps
            </GhostAction>
            <GhostAction as='button' type='button' onClick={copyAddress}>
              <ContentCopyRoundedIcon style={{ fontSize: 16 }} />
              Copy address
            </GhostAction>
          </Actions>
        )}

        {status === 'ready' && (
          <Hint>Click the map to zoom with your scroll wheel. Drag to move around.</Hint>
        )}
      </Details>
    </Card>
  )
}

export default LocationCard

const Card = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.35fr) minmax(240px, 1fr);
  border: 1px solid ${line.base};
  border-radius: ${radius.md};
  background: ${surface.card};
  overflow: hidden;

  @media (max-width: ${bpToken.mobile}) {
    grid-template-columns: 1fr;
  }
`

const MapPane = styled.div`
  position: relative;
  isolation: isolate;
  z-index: 0;
  min-height: 260px;
  background: ${surface.wash};
`

const MapCanvas = styled.div`
  position: absolute;
  inset: 0;

  .leaflet-container {
    font: inherit;
  }
`

const NoMap = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
  font-size: ${font.body};
  color: ${ink.mute};
`

const Details = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
  border-left: 1px solid ${line.base};
  min-width: 0;

  @media (max-width: ${bpToken.mobile}) {
    border-left: none;
    border-top: 1px solid ${line.base};
  }
`

const DetailHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const DetailTitle = styled.span`
  font-size: ${font.emphasis};
  font-weight: 700;
  color: ${ink.base};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Address = styled.p`
  margin: 0;
  font-size: ${font.body};
  line-height: 1.5;
  color: ${ink.soft};
`

const Meta = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
`

const MetaLabel = styled.span`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const MetaValue = styled.span`
  font-size: ${font.body};
  font-variant-numeric: tabular-nums;
  color: ${ink.base};
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`

const PrimaryAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 14px;
  border-radius: ${radius.sm};
  background: ${({ $accent }) => $accent};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  text-decoration: none;
  transition: ${transition};

  &:hover { filter: brightness(0.94); }
`

const GhostAction = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 38px;
  padding: 0 14px;
  border: 1px solid ${line.base};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-family: inherit;
  font-size: ${font.body};
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: ${transition};

  &:hover { border-color: rgba(51, 190, 204, 0.55); color: #1f8c97; }
`

const Hint = styled.p`
  margin: 2px 0 0;
  font-size: ${font.small};
  color: ${ink.mute};
`
